// Pricing math, extracted from pricing-page.tsx so it can be unit-tested in
// isolation (the page itself transitively imports icon packages that don't
// resolve cleanly under the `node` test environment).
//
// Single source of truth = the shared PLANS catalog (`plan.price`).
import { getPlan } from "@reactive-resume/utils";

export type PaidTierId = "pro" | "ultimate";
export type PricingCycle = "weekly" | "monthly" | "yearly";

export type PaidAmounts = {
  /** Per-month figure for the big number on the card. */
  monthly: number;
  /** Per-month-equivalent of the annual plan (annual total ÷ 12). */
  yearly: number;
  /** The annual total used in the "billed $X/year" subcopy. */
  yearlyTotal: number;
  /** Present only for PRO (weekly plan). */
  weekly?: number;
  /** Annual "save N%" badge value. */
  saveYearlyPct?: number;
};

/**
 * Monthly application credit count to DISPLAY for a tier. Reads straight from
 * the PLANS `autoApplications.perMonth` allotment the server enforces.
 */
export const displayCredits = (_tier: "FREE" | "PRO" | "ULTIMATE", perMonth: number): number =>
  perMonth;

export const paidAmounts = (tierId: PaidTierId): PaidAmounts => {
  const plan = getPlan(tierId === "pro" ? "PRO" : "ULTIMATE");
  return {
    monthly: plan.price.monthly,
    yearly: plan.price.yearly / 12,
    yearlyTotal: plan.price.yearly,
    weekly: plan.price.weekly,
    saveYearlyPct: plan.price.savePct?.yearly,
  };
};

/** Prefer the lowest-commitment paid option unless a visitor chose another. */
export const resolvePricingCycle = (
  requested: string | null,
  weeklyAvailable: boolean,
): PricingCycle => {
  if (requested === "yearly") return "yearly";
  if (requested === "monthly") return "monthly";
  if (requested === "weekly" && weeklyAvailable) return "weekly";
  return weeklyAvailable ? "weekly" : "monthly";
};

/** Encode every non-default cycle so clicking a tab round-trips correctly. */
export const pricingCycleSearchParams = (
  next: PricingCycle,
  weeklyAvailable: boolean,
): { billing?: string } => {
  const defaultCycle: PricingCycle = weeklyAvailable ? "weekly" : "monthly";
  return next === defaultCycle ? {} : { billing: next };
};

/** Change the billing cycle without discarding acquisition attribution. */
export const mergePricingCycleSearchParams = (
  current: URLSearchParams,
  next: PricingCycle,
  weeklyAvailable: boolean,
): URLSearchParams => {
  const merged = new URLSearchParams(current);
  merged.delete("billing");
  const { billing } = pricingCycleSearchParams(next, weeklyAvailable);
  if (billing) merged.set("billing", billing);
  return merged;
};
